import { camelCaseToTitleCase } from '../../../util/string';

// Basic type guards/helpers
export const isContainer = (v: any) => v && typeof v === 'object';

export const parseJsonSafe = (r?: string | null) => {
  if (!r) return undefined;
  try { return JSON.parse(r); } catch { return r; }
};

export const getDisplayLabelForItem = (v: any, index: number) => {
  let display = String(index);
  if (v && typeof v === 'object') {
    const tn = (v as any).tableName ?? (v as any).TableName ?? (v as any).name ?? (v as any).Name;
    if (typeof tn === 'string' && tn.trim().length > 0) return camelCaseToTitleCase(tn);
    const keys = Object.keys(v as any);
    if (keys.length === 1) return camelCaseToTitleCase(keys[0]);
  }
  return camelCaseToTitleCase(display);
};

// Keys to omit from Details view
// - Exclude any key containing the exact substring "Id" (case sensitive)
// - Also exclude specific meta fields regardless of case: tableName, entityId
// - Also exclude sensitive fields: password, tokens
export const isSensitiveField = (k: string) => {
  if (!k) return false;
  const lower = k.toLowerCase();
  return lower === 'password' || lower === 'passwordresettoken' || lower === 'resettokenvaliddate';
};

export const shouldOmitInDetailsKey = (k: string) => {
  if (!k) return false;
  if (k.includes('Id')) return true; // case-sensitive match
  const lower = k.toLowerCase();
  return lower === 'tablename' || lower === 'entityid' || isSensitiveField(k);
};

// Display formatting
export const formatDisplayString = (s: string) => camelCaseToTitleCase(s);

export const formatVal = (val: any): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return formatDisplayString(val);
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  try { return JSON.stringify(val); } catch { return String(val); }
};

export const isEmptyValue = (val: any): boolean => {
  if (val === null || val === undefined) return true;
  if (typeof val === 'string') return val.trim().length === 0;
  if (Array.isArray(val)) return val.length === 0;
  if (typeof val === 'object') return Object.keys(val).length === 0;
  return false;
};

export const shouldShowNew = (newVal: any, oldVal: any): boolean => {
  // Show when new has a value OR when old previously had a value (to indicate it was cleared)
  return !isEmptyValue(newVal) || !isEmptyValue(oldVal);
};

// Recursively search an orderItem JSON section for an ItemId string
// Looks for keys named "itemId" (any casing) or nested { item: { id } }
export const findItemIdInSection = (section: any): string | undefined => {
  if (section == null) return undefined;
  if (Array.isArray(section)) {
    for (const el of section) {
      const found = findItemIdInSection(el);
      if (found != null) return found;
    }
    return undefined;
  }
  if (typeof section === 'object') {
    for (const [k, v] of Object.entries(section)) {
      const lower = k.toLowerCase();
      if (lower === 'itemid') {
        if (v != null && (typeof v === 'string' || typeof v === 'number')) return String(v);
      }
      if (lower === 'item' && v && typeof v === 'object') {
        const idVal = (v as any).id;
        if (idVal != null && (typeof idVal === 'string' || typeof idVal === 'number')) return String(idVal);
      }
    }
    for (const v of Object.values(section)) {
      const found = findItemIdInSection(v);
      if (found != null) return found;
    }
    return undefined;
  }
  return undefined;
};

// Helper: recursively remove any object fields whose key contains "id" (case-insensitive) or is sensitive
export const omitIdFields = (val: any): any => {
  if (Array.isArray(val)) {
    return val.map(omitIdFields);
  }
  if (val && typeof val === 'object') {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(val)) {
      if (k.toLowerCase().includes('id')) continue; // exclude keys containing "id"
      if (isSensitiveField(k)) continue; // exclude sensitive fields
      out[k] = omitIdFields(v);
    }
    return out;
  }
  return val;
};

// Try to override accordion titles for Order Item using itemsForLogsArray
// NOTE: This function expects an ItemId as a string extracted from the orderItem JSON
export const tryGetOrderItemTitle = (display: string, itemId: string | undefined, itemsForLogsArray: any[] | undefined): string | undefined => {
  if (display !== 'Order Item') return undefined;
  if (!itemId) return undefined;
  const targetId = String(itemId);
  const match = (itemsForLogsArray || []).find((it: any) => String(it?.id) === targetId);
  return match?.name || undefined;
};
